import {
	loadScanIntervalMin,
	useAgentGatewayStats,
	useMyAgent,
	useMyHuntHfRange,
	useMyHuntTargets,
	type HuntTargetUi,
} from "@/hooks/useAgentGateway";
import {
	useGetPublicLiquidatableVaults,
	type LiquidatableVaultUi,
} from "@/hooks/usePublicProtocol";
import { useAuth } from "@/lib/auth";
import {
	executeAutopilotLiquidate,
	loadAutopilotEnabled,
	serializeHuntTargets,
} from "@/lib/agent-autopilot";
import { appendHuntScanActivity } from "@/lib/hunt-activity-log";
import {
	defaultHuntTargets,
	vaultMatchesHfRange,
	vaultMatchesHunt,
} from "@/lib/hunt-match";
import { ROUTES } from "@/lib/routes";
import type { Identity } from "@icp-sdk/core/agent";
import { useQueryClient } from "@tanstack/react-query";
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	useSyncExternalStore,
} from "react";
import { toast } from "sonner";

const LAST_SCAN_KEY = "motokomaker.v2.agentHuntLastScanMs";

export type HuntMode = "idle" | "watch" | "autopilot";

export type HuntScanSnap = {
	lastScanMs: number | null;
	lastMatchCount: number | null;
	lastError: string | null;
	lastAutopilotResult: string | null;
	mode: HuntMode;
	running: boolean;
};

type HuntControllerConfig = {
	enabled: boolean;
	autopilot: boolean;
	identity: Identity | null;
	huntTargets: HuntTargetUi[];
	hfMinE8: bigint;
	hfMaxE8: bigint;
	commissionBps: number;
	usdmBalance: number;
	refetchLiquidatable: () => Promise<{ data?: LiquidatableVaultUi[] }>;
	ensureProtocol: () => Promise<{
		actor: Parameters<typeof executeAutopilotLiquidate>[0]["protocol"];
		agent: Parameters<typeof executeAutopilotLiquidate>[0]["httpAgent"];
	} | null>;
	refreshBalances: () => void | Promise<void>;
	onInvalidate: () => void;
};

let snap: HuntScanSnap = {
	lastScanMs: (() => {
		try {
			const v = Number(localStorage.getItem(LAST_SCAN_KEY));
			return Number.isFinite(v) && v > 0 ? v : null;
		} catch {
			return null;
		}
	})(),
	lastMatchCount: null,
	lastError: null,
	lastAutopilotResult: null,
	mode: "idle",
	running: false,
};

const listeners = new Set<() => void>();
let generation = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let scanInFlight = false;
let activeConfig: HuntControllerConfig | null = null;
let visibilityBound = false;

function publish(partial: Partial<HuntScanSnap>) {
	snap = { ...snap, ...partial };
	for (const l of listeners) l();
}

function subscribe(cb: () => void) {
	listeners.add(cb);
	return () => listeners.delete(cb);
}

function getSnap() {
	return snap;
}

function clearTimer() {
	if (timer != null) {
		clearTimeout(timer);
		timer = null;
	}
}

function scheduleNext(gen: number) {
	if (gen !== generation || !activeConfig?.enabled) return;
	clearTimer();
	const min = loadScanIntervalMin();
	timer = setTimeout(() => {
		void runScan(gen).finally(() => scheduleNext(gen));
	}, min * 60_000);
}

async function runScan(gen: number) {
	const cfg = activeConfig;
	if (!cfg?.enabled || gen !== generation || scanInFlight) return;
	scanInFlight = true;
	publish({ running: true, lastError: null });
	try {
		const result = await cfg.refetchLiquidatable();
		if (gen !== generation) return;
		const list = result.data ?? [];
		const targets =
			cfg.huntTargets.length > 0 ? cfg.huntTargets : defaultHuntTargets();
		const matches = list
			.filter(
				(v) =>
					vaultMatchesHunt(targets, v.collateralLines) &&
					vaultMatchesHfRange(v.cr, cfg.hfMinE8, cfg.hfMaxE8),
			)
			.sort((a, b) => a.cr - b.cr);

		const now = Date.now();
		try {
			localStorage.setItem(LAST_SCAN_KEY, String(now));
		} catch {
			/* ignore */
		}
		const mode = cfg.autopilot ? "autopilot" : "watch";
		publish({
			lastScanMs: now,
			lastMatchCount: matches.length,
			mode,
		});
		appendHuntScanActivity({
			ts: now,
			mode,
			matchCount: matches.length,
			vaultIds: matches.slice(0, 5).map((v) => v.id.toString()),
		});

		if (matches.length === 0) return;

		// Opportunity / Liquidate CTA toast is Watch-only — never in Autopilot.
		if (!cfg.autopilot) {
			toast.message(
				`${matches.length} opportunit${matches.length === 1 ? "y" : "ies"} matching hunt`,
				{
					description: "Watch mode — open Liquidate to review (no auto-spend).",
					action: {
						label: "Liquidate",
						onClick: () => {
							window.location.assign(ROUTES.liquidation);
						},
					},
				},
			);
			return;
		}

		const target = matches[0]!;
		const pair = await cfg.ensureProtocol();
		if (!pair || !cfg.identity) {
			throw new Error("Protocol session missing — reconnect Internet Identity");
		}
		const out = await executeAutopilotLiquidate({
			identity: cfg.identity,
			protocol: pair.actor,
			httpAgent: pair.agent,
			vaultId: target.id,
			suggestedRepayUsdm: target.maxPartialUsdm,
			commissionBps: cfg.commissionBps,
			usdmWalletBalance: cfg.usdmBalance,
		});
		if (gen !== generation) return;
		const msg = `Autopilot liquidated #${out.vaultId.toString()} · ${out.repayUsdm.toFixed(2)} USDM`;
		publish({ lastAutopilotResult: msg, lastError: null });
		toast.success(msg);
		await cfg.refreshBalances();
		cfg.onInvalidate();
	} catch (e) {
		if (gen !== generation) return;
		const msg = e instanceof Error ? e.message : String(e);
		publish({ lastError: msg });
		if (activeConfig?.autopilot) {
			toast.error(`Autopilot: ${msg}`);
		}
	} finally {
		scanInFlight = false;
		if (gen === generation) publish({ running: false });
	}
}

function onVisibility() {
	if (document.visibilityState !== "visible") return;
	const gen = generation;
	if (!activeConfig?.enabled) return;
	void runScan(gen);
}

export function startHuntController(config: HuntControllerConfig) {
	generation += 1;
	const gen = generation;
	clearTimer();
	activeConfig = config;

	if (!visibilityBound) {
		document.addEventListener("visibilitychange", onVisibility);
		visibilityBound = true;
	}

	if (!config.enabled) {
		publish({ mode: "idle", running: false });
		return;
	}

	publish({
		mode: config.autopilot ? "autopilot" : "watch",
		lastError: null,
	});
	void runScan(gen).finally(() => scheduleNext(gen));
}

export function stopHuntController() {
	generation += 1;
	clearTimer();
	activeConfig = null;
	scanInFlight = false;
	publish({ mode: "idle", running: false });
}

export function updateHuntControllerConfig(
	patch: Partial<HuntControllerConfig>,
) {
	if (!activeConfig) return;
	activeConfig = { ...activeConfig, ...patch };
	publish({
		mode: !activeConfig.enabled
			? "idle"
			: activeConfig.autopilot
				? "autopilot"
				: "watch",
	});
}

/** Read-only scan status for AgentsPage. */
export function useAgentHuntScanner() {
	return useSyncExternalStore(subscribe, getSnap, getSnap);
}

/**
 * Single host — mount once under App (inside Router + Auth + QueryClient).
 */
export function AgentHuntHost() {
	const { isAuthenticated, identity, balances, ensureProtocolActor, refreshBalances } =
		useAuth();
	const { data: agent } = useMyAgent();
	const { data: huntTargets = [] } = useMyHuntTargets();
	const { data: hfRange } = useMyHuntHfRange();
	const { data: stats } = useAgentGatewayStats();
	const { refetch } = useGetPublicLiquidatableVaults();
	const qc = useQueryClient();
	const [autopilotTick, setAutopilotTick] = useStateTick();

	const huntKey = useMemo(
		() => serializeHuntTargets(huntTargets),
		[huntTargets],
	);
	const enabled = Boolean(isAuthenticated && identity && agent?.active);
	const autopilot = loadAutopilotEnabled() && enabled;
	// re-read when tick changes (AgentsPage toggles)
	void autopilotTick;

	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (
				e.key === "motokomaker.v2.agentAutopilot" ||
				e.key === "motokomaker.v2.agentScanIntervalMin"
			) {
				setAutopilotTick();
			}
		};
		window.addEventListener("storage", onStorage);
		window.addEventListener("motoko-autopilot-changed", setAutopilotTick);
		return () => {
			window.removeEventListener("storage", onStorage);
			window.removeEventListener("motoko-autopilot-changed", setAutopilotTick);
		};
	}, [setAutopilotTick]);

	useEffect(() => {
		if (!enabled || !identity) {
			stopHuntController();
			return;
		}
		startHuntController({
			enabled: true,
			autopilot,
			identity,
			huntTargets:
				huntTargets.length > 0 ? huntTargets : defaultHuntTargets(),
			hfMinE8: hfRange?.minE8 ?? 100_000_000n,
			hfMaxE8: hfRange?.maxE8 ?? 130_000_000n,
			commissionBps: stats ? Number(stats.liqCommissionBps) : 25,
			usdmBalance: balances.USDM,
			refetchLiquidatable: async () => {
				const r = await refetch();
				return { data: r.data };
			},
			ensureProtocol: ensureProtocolActor,
			refreshBalances: () => void refreshBalances(),
			onInvalidate: () => {
				void qc.invalidateQueries({ queryKey: ["v2", "public", "liquidatable"] });
				void qc.invalidateQueries({ queryKey: ["v2", "myAgentCallHistory"] });
				void qc.invalidateQueries({ queryKey: ["v2", "myAgentTxHistory"] });
				void qc.invalidateQueries({ queryKey: ["v2", "myCycleCredits"] });
				void qc.invalidateQueries({ queryKey: ["v2", "myWaivers"] });
			},
		});
		return () => stopHuntController();
		// balances/commission patched via updateHuntControllerConfig — do not restart timer
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional stable loop
	}, [
		enabled,
		autopilot,
		identity,
		huntKey,
		hfRange?.minE8,
		hfRange?.maxE8,
		refetch,
		ensureProtocolActor,
		refreshBalances,
		qc,
	]);

	// Live-update wallet / commission / hunt snapshot without tearing down the timer.
	useEffect(() => {
		updateHuntControllerConfig({
			usdmBalance: balances.USDM,
			commissionBps: stats ? Number(stats.liqCommissionBps) : 25,
			autopilot,
			huntTargets:
				huntTargets.length > 0 ? huntTargets : defaultHuntTargets(),
			hfMinE8: hfRange?.minE8 ?? 100_000_000n,
			hfMaxE8: hfRange?.maxE8 ?? 130_000_000n,
		});
	}, [
		balances.USDM,
		stats?.liqCommissionBps,
		autopilot,
		huntKey,
		huntTargets,
		hfRange?.minE8,
		hfRange?.maxE8,
	]);

	return null;
}

function useStateTick(): [number, () => void] {
	const [n, setN] = useState(0);
	const bump = useCallback(() => setN((x) => x + 1), []);
	return [n, bump];
}

/** Call after toggling autopilot in the same tab (storage events are cross-tab only). */
export function notifyAutopilotChanged() {
	window.dispatchEvent(new Event("motoko-autopilot-changed"));
}
